import { useCallback } from 'react';
import { ERROR_EVENT_NAME } from '@/consts/events';
import analytics from '@/services/Analytics';
import { useRegistration } from '@/context/RegistrationContext';

export type TrackArguments = {
  event: string;
  properties?: Record<string, unknown>;
};

export type TrackFunction = (args: TrackArguments) => void;

/**
 * Analytics hook for tracking user interactions.
 * Integrates with Mixpanel via the Analytics service.
 * Automatically includes webregUserId and UTM params in all events.
 */
const useAnalytics = () => {
  const { webregUserId, utmParams } = useRegistration();

  const track: TrackFunction = useCallback(
    ({ event, properties }) => {
      const defaultProperties = {
        webregUserId,
        utmParams,
      };
      analytics.track(event, { ...properties, ...defaultProperties });
    },
    [webregUserId, utmParams],
  );

  const trackView: TrackFunction = useCallback(
    ({ event, properties }) => {
      track({
        event: `view_${event}`,
        properties,
      });
    },
    [track, webregUserId, utmParams],
  );

  const trackError: TrackFunction = useCallback(
    ({ event, properties }) => {
      const defaultProperties = {
        webregUserId,
        utmParams,
      };
      analytics.trackError(event || ERROR_EVENT_NAME, { ...properties, ...defaultProperties });
    },
    [webregUserId, utmParams],
  );

  const identifyUser = useCallback((analyticsID: string) => {
    analytics.identifyUser(analyticsID);
  }, []);

  const setUserProperties = useCallback((properties: Record<string, unknown>) => {
    analytics.setUserProperties(properties);
  }, []);

  const trackPopupView: TrackFunction = useCallback(
    ({ event, properties }) => {
      track({
        event: `popup_${event}`,
        properties: { ...properties },
      });
    },
    [analytics, webregUserId, utmParams],
  );

  return {
    track,
    trackView,
    trackError,
    identifyUser,
    setUserProperties,
    trackPopupView,
  };
};

export { useAnalytics };
